import { useMemo } from "react";

type Props = {
  html: string;
  /** Si true, fondo blanco (mas fiel al cliente real). Si false, fondo
   * oscuro tipo SPA. */
  light?: boolean;
  /** Altura maxima del preview en CSS (default 24rem = max-h-96). */
  maxHeight?: string;
  className?: string;
  /**
   * `{content-id: data:image/...;base64,...}` de las imágenes incrustadas.
   *
   * Van como datos y no como URL porque este iframe está aislado (`sandbox`
   * vacío, sin origen): una petición suya al servidor NO llevaría la cookie de
   * sesión y la imagen saldría rota. Con los datos dentro no hay petición que
   * autenticar. Lo que no venga en el mapa conserva su marcador.
   */
  cidMap?: Record<string, string>;
  /**
   * `{src original: data:...}` para imágenes que el correo referencia por URL.
   *
   * Existe por la MISMA razón que `cidMap`: este iframe no tiene origen, así
   * que sus peticiones al servidor **no llevan la cookie de sesión** — el
   * servidor contesta «no autorizado» y el navegador tira la respuesta con
   * `ERR_BLOCKED_BY_ORB`. Guardar la imagen y apuntarle con una URL propia NO
   * funciona aquí adentro: hay que traerla desde la página y meterla como
   * datos. (Se aprendió dos veces.)
   */
  srcMap?: Record<string, string>;
};

/**
 * Preview seguro de HTML de correo. Renderea el HTML dentro de un
 * `<iframe sandbox>` para aislar sus estilos del SPA — los emails
 * traen `<html><head><style>...</style>...<body>` completos y al
 * meterlos via `dangerouslySetInnerHTML` directo, el `<style>` se
 * aplica globalmente y pisa el tema del SPA (pantalla en blanco).
 *
 * Plus: limpia URLs `cid:...` (imagenes embebidas que el browser no
 * puede resolver) reemplazandolas por un placeholder visible para que
 * el usuario sepa que hay un attachment en el correo original.
 *
 * Sandbox: SIN `allow-scripts` (no ejecuta JS embebido — el HTML de un
 * forward externo no es confiable). SIN `allow-same-origin` tampoco
 * (el iframe es estrictamente sandboxed; cualquier <img src="https://...">
 * se carga via el origin nulo del sandbox y sigue funcionando porque
 * no requiere cookies).
 */
export default function EmailHtmlPreview({
  html,
  light = true,
  maxHeight = "24rem",
  className = "",
  cidMap,
  srcMap,
}: Props) {
  const srcdoc = useMemo(
    () => prepararSrcdoc(html, light, cidMap, srcMap),
    [html, light, cidMap, srcMap],
  );
  return (
    <iframe
      // Nombre accesible con aria-label, no con `title` (regla de cero tooltips).
      aria-label="Vista previa del correo"
      srcDoc={srcdoc}
      // sandbox vacio = todas las restricciones activas. Suficiente
      // para mostrar HTML/CSS y cargar imagenes; bloquea JS, forms,
      // navegacion top, popups, etc.
      sandbox=""
      className={`w-full rounded border ${
        light ? "bg-white border-zinc-300" : "bg-ink-800 border-ink-700"
      } ${className}`}
      style={{ height: maxHeight }}
    />
  );
}

/** Construye el documento HTML completo que va dentro del srcdoc:
 * tema base + el HTML del correo (con cid: limpiado). */
function prepararSrcdoc(
  html: string,
  light: boolean,
  cidMap?: Record<string, string>,
  srcMap?: Record<string, string>,
): string {
  const limpio = reemplazarSrc(reemplazarCids(html, cidMap), srcMap);
  // Wrap minimo con base font + colores. El HTML del correo puede tener
  // su propio body/style; eso lo respetamos. Pero si el correo no tiene
  // estilos (correo de texto plano convertido), al menos el body luce
  // razonable.
  const bg = light ? "#ffffff" : "#27272a";
  const fg = light ? "#18181b" : "#e4e4e7";
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<base target="_blank">
<style>
  html, body {
    margin: 0;
    padding: 12px;
    background: ${bg};
    color: ${fg};
    font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    word-wrap: break-word;
  }
  img { max-width: 100%; height: auto; }
  /* Placeholder para imagenes cid: que el browser no puede cargar */
  .arktickets-cid-fallback {
    display: inline-block;
    padding: 4px 8px;
    margin: 4px 2px;
    border: 1px dashed #94a3b8;
    border-radius: 4px;
    background: #f1f5f9;
    color: #475569;
    font-size: 11px;
    font-family: monospace;
  }
  a { color: #0d9488; }
</style>
</head>
<body>
${limpio}
</body>
</html>`;
}

/** Reemplaza `<img src="cid:...">` por un placeholder visible. El
 * browser NO sabe resolver el esquema `cid:` (es Content-ID del MIME
 * original del correo) y dejarlo intentaria fetch fallido. */
function reemplazarCids(html: string, cidMap?: Record<string, string>): string {
  return html.replace(
    /<img\b[^>]*src=["']cid:([^"']+)["'][^>]*>/gi,
    (etiqueta, cid) => {
      // El Content-ID llega con picoparéntesis en unos correos y sin ellos en
      // otros; se prueban las dos formas antes de darlo por perdido.
      const clave = String(cid).trim();
      const datos = cidMap?.[clave] ?? cidMap?.[clave.replace(/^<|>$/g, "")];
      if (datos) {
        // Se conserva la etiqueta original (con su width/style) y solo se le
        // cambia el src: así la imagen respeta el tamaño que le puso el correo
        // en vez de reventar el ancho del cuerpo.
        return String(etiqueta).replace(
          /src=["']cid:[^"']+["']/i,
          `src="${datos}" style="max-width:100%;height:auto"`,
        );
      }
      return `<span class="arktickets-cid-fallback">📎 imagen adjunta · ${escapeHtml(
        clave.slice(0, 60),
      )}</span>`;
    },
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Cambia el `src` de las imágenes que estén en el mapa, dejando el resto igual. */
function reemplazarSrc(html: string, srcMap?: Record<string, string>): string {
  if (!srcMap || !Object.keys(srcMap).length) return html;
  return html.replace(/src=["']([^"']+)["']/gi, (etiqueta, url) => {
    const datos = srcMap[String(url)];
    return datos ? `src="${datos}" style="max-width:100%;height:auto"` : etiqueta;
  });
}
